'use client';

import type { Collection } from '@/app/actions/collection';
import type { UpdatingCriteriasState, UpdatingDataCriteria } from '~/components/Profile/model';
import type { CriteriaChoiceUserType } from '~/model/CriteriaChoiceType';
import type { CriteriaType } from '~/model/CriteriaType';
import { VpButton, VpFormControl, VpFormError, VpFormLabel, VpInput } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { fetchItem } from '@/app/actions/server';
import { useApiErrorContext, useAuthenticatedContext, useUserDataContext } from '@/app/components/Provider';
import { useUserCriteria } from '@/app/hooks/useUserCriteria';
import { GenericDialog } from '~/components/commons/genericDialog/GenericDialog';
import { Toaster } from '~/components/Profile/Toaster';
import { getMatchingUserCriteria, isSubmittable } from '~/components/Profile/utils';
import { apiTypes } from '~/const/api';
import { useRequestState } from '~/hooks/request/useRequestState';
import { getFormattedAnswerValue } from '~/utils/utils';
import { Criteria } from './Criteria/Criteria';
import { Submit } from './Submit';

export const PersonalInformations = ({
  criteria,
  criteriaChoiceUser,
}: {
  criteria: CriteriaType[];
  criteriaChoiceUser: Promise<Collection<CriteriaChoiceUserType>>;
}) => {
  const tProfile = useTranslations('PROFILE');
  const tForm = useTranslations('FORM');

  const { user, setUser } = useAuthenticatedContext();
  const { setError } = useApiErrorContext();

  const { status, startRequest, handleSuccess, handleError } = useRequestState();

  const [userCriteria, setUserCriteria] = useState<CriteriaChoiceUserType[]>();
  const [openDialog, setOpenDialog] = useState(false);
  const [isToasterVisible, setIsToasterVisible] = useState(false);
  const [userUpdatingData, setUserUpdatingData] = useState(null);
  const [updatingData, setUpdatingData] = useState<UpdatingCriteriasState>({});

  const { update } = useUserDataContext();
  const { submit } = useUserCriteria({});

  const confirmationDialogContent = (
    <p>{tProfile('PERSONAL_DATA.CONTENT_CONFIRM_CHANGE_MAIL')}</p>
  );

  const updateUserDataInState = (keyAttribute, e) => {
    const newUserUpdatingData = {
      ...userUpdatingData,
      [keyAttribute]: getFormattedAnswerValue(e.target.value, apiTypes.TEXT),
    };
    setUserUpdatingData(newUserUpdatingData);
  };

  const canSubmitCriteria
    = Object.keys(updatingData).length > 0
      && !Object.values(updatingData).every(d => d.ignore === true);

  const handleChangeCriteria = ({
    value,
    type,
    criteriaUri,
    ignore,
  }: UpdatingDataCriteria) => {
    // Get the corresponding user criteria choice id if it exists
    const userCriteriaChoiceId = userCriteria.find(
      uc => uc.criterion['@id'] === criteriaUri,
    )?.id;

    // Update data in state
    setUpdatingData((state) => {
      return {
        // add or update criteria otherwise
        ...state,
        [criteriaUri]: {
          criteriaUri,
          type,
          value,
          ignore,
          // add user criteria choice id if it exists
          ...(userCriteriaChoiceId && { userCriteriaChoiceId }),
        },
      };
    });
  };

  const valueFirstName = userUpdatingData?.firstname || '';
  const valueLastName = userUpdatingData?.lastname || '';
  const valueAddress = userUpdatingData?.address || '';
  const valueCity = userUpdatingData?.city || '';
  const valueZipcode = userUpdatingData?.zipcode || '';
  const valuePhone = userUpdatingData?.phone || '';
  const valueMail = user?.mail || '';

  const inputForbiddenChars = /[!#$%&'()*+,./:;<=>?@[\]^_`{|}~]/g;
  const inputMaxLength = 40;

  const errorToDisplayed = (value: string) => {
    return {
      empty: !value,
      forbiddenChars: inputForbiddenChars.test(value),
      maxLength: value.length > inputMaxLength,
    };
  };

  const firstnameErrorToDisplayed = errorToDisplayed(valueFirstName);
  const lastnameErrorToDisplayed = errorToDisplayed(valueLastName);

  const isFirstnameErrorDisplayed = Object.values(firstnameErrorToDisplayed).some(reason => reason);
  const isLastnameErrorDisplayed = Object.values(lastnameErrorToDisplayed).some(reason => reason);

  const canBeSubmitted
    = (!isFirstnameErrorDisplayed && !isLastnameErrorDisplayed)
      && (isSubmittable(userUpdatingData, user) || canSubmitCriteria);

  const handleUserPutData = async () => {
    const putData = {
      firstname: userUpdatingData.firstname,
      lastname: userUpdatingData.lastname,
      address: userUpdatingData.address,
      city: userUpdatingData.city,
      zipcode: userUpdatingData.zipcode,
      phone: userUpdatingData.phone,
    };

    if (updatingData) {
      await submit(updatingData);
      await update();
      setUpdatingData({});
    }

    startRequest();
    const response = await fetchItem({
      pathname: `users/${user?.id}`,
      method: 'PUT',
      body: putData,
    });

    if (response['hydra:description']) {
      handleError();
      return setError(response);
    }

    setUserUpdatingData(response);
    setIsToasterVisible(true);
    setUser(response);
    handleSuccess();
  };

  const inputHelperText = (
    fieldname: string,
    value: string,
    error: ReturnType<typeof errorToDisplayed>,
  ) => {
    if (error.forbiddenChars) {
      const forbiddenCharsSet = new Set<string>();
      const forbiddenMatches = value.match(inputForbiddenChars);

      if (forbiddenMatches)
        forbiddenMatches.forEach(char => forbiddenCharsSet.add(char));

      return tProfile('CONTENT.ERROR.FORBIDDEN_CHARS', {
        chars: Array.from(forbiddenCharsSet).join(''),
      });
    } else if (error.maxLength) {
      return tProfile('CONTENT.ERROR.MAX_LENGTH', {
        fieldname: fieldname.toLowerCase(),
        max: inputMaxLength,
      });
    }

    return tProfile('CONTENT.MANDATORY_DATA');
  };

  useEffect(() => {
    if (user?.id && !userUpdatingData)
      setUserUpdatingData(user);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (isToasterVisible) {
      const timer = setTimeout(() => setIsToasterVisible(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isToasterVisible]);

  useEffect(() => {
    criteriaChoiceUser.then((data) => {
      setUserCriteria(data?.['hydra:member']);
    });
  }, [criteriaChoiceUser]);

  return (
    <>
      <div className="profile__fieldgroup">
        <div className="profile__field">
          <VpFormControl>
            <VpFormLabel>{tProfile('CONTENT.FIRSTNAME')}</VpFormLabel>
            <VpInput
              id="firstName"
              value={valueFirstName}
              onChange={e => updateUserDataInState('firstname', e)}
              data-testid="firstName"
            />
            {isFirstnameErrorDisplayed && (
              <VpFormError>
                {inputHelperText(
                  tProfile('CONTENT.FIRSTNAME'),
                  valueFirstName,
                  firstnameErrorToDisplayed,
                )}
              </VpFormError>
            )}
          </VpFormControl>
        </div>
        <div className="profile__field">
          <VpFormControl>
            <VpFormLabel>{tProfile('CONTENT.LASTNAME')}</VpFormLabel>
            <VpInput
              id="lastName"
              value={valueLastName}
              onChange={e => updateUserDataInState('lastname', e)}
              data-testid="lastName"
            />
            {isLastnameErrorDisplayed && (
              <VpFormError>
                {inputHelperText(
                  tProfile('CONTENT.LASTNAME'),
                  valueLastName,
                  lastnameErrorToDisplayed,
                )}
              </VpFormError>
            )}
          </VpFormControl>
        </div>
        <div className="profile__field">
          <VpFormControl>
            <VpFormLabel>{tProfile('CONTENT.PHONE')}</VpFormLabel>
            <VpInput
              id="phone"
              value={valuePhone}
              onChange={e => updateUserDataInState('phone', e)}
              data-testid="phone"
            />
          </VpFormControl>
        </div>
        <div className="profile__field profile__field--row">
          <span id="mail">
            {tProfile('CONTENT.MAIL')}
            {' '}
            :
            {' '}
            {valueMail}
          </span>
          <VpButton
            onClick={() => setOpenDialog(true)}
            data-testid="email"
            variant="tertiary"
          >
            {tProfile('PERSONAL_DATA.CHANGE_MAIL')}
          </VpButton>
        </div>
      </div>
      <div className="profile__fieldgroup">
        <div className="profile__field">
          <VpFormControl>
            <VpFormLabel>{tProfile('CONTENT.ADDRESS')}</VpFormLabel>
            <VpInput
              id="street"
              value={valueAddress}
              onChange={e => updateUserDataInState('address', e)}
              data-testid="street"
            />
          </VpFormControl>
        </div>
        <div className="profile__field profile__field--col-2">
          <div>
            <VpFormControl>
              <VpFormLabel>{tForm('USER_DATA_ZIPCODE')}</VpFormLabel>
              <VpInput
                id="zipcode"
                value={valueZipcode}
                onChange={e => updateUserDataInState('zipcode', e)}
                data-testid="zipcode"
              />
            </VpFormControl>
          </div>
          <div>
            <VpFormControl>
              <VpFormLabel>{tForm('USER_DATA_CITY')}</VpFormLabel>
              <VpInput
                id="city"
                value={valueCity}
                onChange={e => updateUserDataInState('city', e)}
                data-testid="city"
              />
            </VpFormControl>
          </div>
        </div>
        {(!userCriteria?.length) && [...Array.from({ length: 2 }).keys()].map(i => (
          <div key={i} className="profile__field">
            <span
              className="vtmn-skeleton vtmn-skeleton_line"
              style={{ width: '100%', height: '52px' }}
            />
          </div>
        ))}
        {!!userCriteria && criteria?.map((criterion) => {
          const updatingUserValue = updatingData[criterion['@id']]?.value;
          const matchingUserCriteria: CriteriaChoiceUserType
            = getMatchingUserCriteria(criterion, userCriteria);

          return (
            <Criteria
              key={`criterion-${criterion.id}`}
              criteria={criterion}
              userCriteria={matchingUserCriteria}
              updatingUserValue={updatingUserValue}
              handleChange={handleChangeCriteria}
            />
          );
        })}
      </div>
      <div>
        <GenericDialog
          isOpen={openDialog}
          isClosable={false}
          data-testid="GenericDialogMail"
          primaryButtonTitle={tProfile('GLOBAL.OK_BUTTON')}
          primaryButtonClick={() => {
            window.open(
              'https://www.decathlon.fr/account/dashboard',
              '_blank',
              'noopener',
            );
            setOpenDialog(false);
          }}
          secondaryButtonTitle={tProfile('GLOBAL.CANCEL')}
          secondaryButtonClick={() => setOpenDialog(false)}
          content={confirmationDialogContent}
          title={tProfile('PERSONAL_DATA.CHANGE_MAIL_2')}
        />
      </div>
      {isToasterVisible && <Toaster />}
      <Submit
        user={user}
        handleUserPutData={handleUserPutData}
        canBeSubmitted={canBeSubmitted && status !== 'pending'}
        isLoading={status === 'pending'}
        setUserUpdatingData={setUserUpdatingData}
        setUpdatingData={setUpdatingData}
      />
    </>
  );
};
